import { observer } from 'mobx-react-lite';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, CardContent, Container, Link, Stack, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useAuthStore } from '../../stores/root_store';

// Where the application starts once someone has an account. ApplyPage moves
// people along to the step that matches their status from there.
const FIRST_STEP = '/apply/details';
const NEXT = `?next=${encodeURIComponent(FIRST_STEP)}`;

const steps = [
  {
    icon: <PersonOutlineIcon fontSize="large" color="primary" />,
    title: 'Tell us about you',
    description: 'Your name, address, resume, and a few quick questions.',
  },
  {
    icon: <AssignmentOutlinedIcon fontSize="large" color="primary" />,
    title: 'Do a sample task',
    description: 'Reply to a real-world request the way you would for a client.',
  },
  {
    icon: <FactCheckOutlinedIcon fontSize="large" color="primary" />,
    title: 'We review it',
    description: "Our team reads every application and emails you when it's decided.",
  },
];

// Public landing page for assistant applicants. Logged-in visitors go straight
// into the application.
export const ApplyIntroPage = observer(() => {
  const authStore = useAuthStore();

  if (authStore.isAuthenticated) {
    return <Navigate to={FIRST_STEP} replace />;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Work with Fancy Hands
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
          Help busy people get things done, from home, on your own schedule.
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 6 }}>
        {steps.map((step, i) => (
          <Card key={step.title} variant="outlined" sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              {step.icon}
              <Typography variant="overline" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Step {i + 1}
              </Typography>
              <Typography variant="h6" gutterBottom>
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Box sx={{ textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          You'll need to live in the US and be legally able to work here.
        </Typography>
        <Button
          component={RouterLink}
          to={`/register${NEXT}`}
          variant="contained"
          size="large"
        >
          Create account to apply
        </Button>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Already have an account?{' '}
          <Link component={RouterLink} to={`/login${NEXT}`}>
            Sign in to apply
          </Link>
        </Typography>
      </Box>
    </Container>
  );
});
